import { Component, ComponentFactoryResolver, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { CtrlBaseComponent } from '../ctrl-base.component';
import { ADN_PLUS, Action } from '../../models';
import { CtMessageComponent } from '../ct-message/ct-message.component';
import { PfComponents } from '../../enums/pfComponents';
import { CtMessageType, CtMessageSize, CtMessageStyle, CtMessageVisualization } from '../../enums/plusMessage';
import { CtButtonComponent } from '../ct-button/ct-button.component';



@Component({
  selector: 'ct-message-container',
  templateUrl: './ct-message-container.component.html',
  styleUrls: ['./ct-message-container.component.css']
})
export class CtMessageContainerComponent extends CtrlBaseComponent implements OnInit {

  @ViewChild('messagesListContainer', { read: ViewContainerRef }) messageListContainer: ViewContainerRef;
  @ViewChild('presentarBtn', { read: ViewContainerRef }) presentarBtnContainer: ViewContainerRef;


  private title: string;
  private actionText: string;
  private actionIcon: string;
  private action: Action;
  private value: ADN_PLUS[] = [];
  public isPendPlanBtn: boolean = false;


  constructor(private cfr: ComponentFactoryResolver) {
    super();
  }

  /**
   * Configures the action event for the message container.
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.action = this.getEvent('onClick');

  }



  private createComponent(container: ViewContainerRef, componentReference: any, metadata: any) {
    container.clear();
    const componentFactory = this.cfr.resolveComponentFactory(componentReference);
    const componentRef = container.createComponent(componentFactory);
    const component: any = componentRef.instance;
    component.setMetaData(metadata);
    return component;
  }

  /**
   * Creates and adds message components to the container based on the provided ADN_PLUS array.
   * @param {ADN_PLUS[]} response - Array of ADN_PLUS objects containing message data.
   */
  private createMessages(response: ADN_PLUS[]): void {

    const responseComponents: any = this.getResponseComponents(response);

    if (!responseComponents) return;
    this.setDataToContainer(responseComponents);
    this.paintChildren(this.getMetaData(), this.messageListContainer);
  }

  /**
   * Transforms the given ADN_PLUS array into an array of CtMessageMetadata objects.
   * @param {ADN_PLUS[]} response - Array of ADN_PLUS objects containing message data.
   * @return {any[]} - Array of CtMessage metadata objects.
   */
  private getResponseComponents(response: ADN_PLUS[]): any[] {
    if (!response) return;
    return response
      .map((adn, index) => {
        const { title, message, type } = this.getDataFromAdn(adn);

        if (!message || !type) {
          return null;
        }

        const ctMessageMetadata = this.getCtMessageMetadata(message, type, title, index, adn);
        if (this.action) {
          this.action.conditionParams = [ctMessageMetadata.controlProperties.id];
          ctMessageMetadata.controlActions[0] = 
          JSON.parse(JSON.stringify(this.action));
        }

        return ctMessageMetadata;
      })
      .filter(metadata => metadata !== null);
  }

  /**
   * Extracts title, message, and type from the given ADN_PLUS object.
   * @param {ADN_PLUS} adn - ADN_PLUS object containing message data.
   * @return {{ title: string, message: string, type: string }} - Extracted title, message, and type.
   */
  private getDataFromAdn(adn: ADN_PLUS): { title: string, message: string, type: string } {
    const message: string = this.utilService.getStringAttribute('MESSAGE', adn.ArrayEntities.List);
    const title: string = this.utilService.getStringAttribute('TITLE', adn.ArrayEntities.List);
    const type: string = this.utilService.getStringAttribute('TYPE', adn.ArrayEntities.List);

    return { title, message, type };
  }

  /**
   * Creates a CtMessage metadata object with the given parameters.
   * @param {string} message - Message text.
   * @param {string} type - Message type.
   * @param {string} title - Message title.
   * @param {number} index - Index of the message in the response array.
   * @param {ADN_PLUS} adn - ADN_PLUS object containing message data.
   * @return {any} - metadata object of the CtMessage.
   */
  private getCtMessageMetadata(message: string, type: string, title: string, index: number, adn: ADN_PLUS): any {
    const isTypeSuccess = type === CtMessageType.success;
    this.isPendPlanBtn = false;

    const metadata = {
      ctType: PfComponents.CtMessage,
      controlProperties: {
        actionIcon: isTypeSuccess ? '' : this.actionIcon,
        actionText: isTypeSuccess ? '' : this.actionText,
        hidden: false,
        canBeClosed: false,
        size: CtMessageSize.large,
        style: CtMessageStyle.multicolor,
        type,
        visualization: CtMessageVisualization.block,
        message,
        title,
        id: `${this.getViewId()}_${PfComponents.CtMessage}_default_${index}`,
        value: adn
      },
      controlActions: isTypeSuccess ? [] : this.action != null ? [this.action] : [],
      models: []
    };
    return metadata;
  }

  /**
   * Sets the message component's metadata with the new array of CtMessageMetadata objects.
   * @param {CtMessageMetadata[]} newMessageChildren - Array of CtMessageMetadata objects.
   */
  private setDataToContainer(newMessageChildren: any[]): void {
    this.getMetaData().models = newMessageChildren;
    this.setChildrenToParentContainer();
  }

  /**
   * Initializes and adds children instances (CtMessageComponent) to the parent component.
   */
  private setChildrenToParentContainer(): void {
    const childrenInstances = this.getMetaData().models.map((child: any) => {
      const messageComponentInstance = new CtMessageComponent();
      messageComponentInstance.setMetaData(child);
      messageComponentInstance.ngOnInit();
      return messageComponentInstance;
    });

    this.addChildren(childrenInstances);
  }

  //getters and setters
  public getTitle(): string { return this.title; }
  public getValue(): ADN_PLUS[] { return this.value; }
  public getActionText(): string { return this.actionText; }
  public getActionIcon(): string { return this.actionIcon; }

  public setTitle(title: string): void { this.title = title; }
  public setValue(value: ADN_PLUS[]): void {
    this.value = value;
    this.createMessages(value);
  }
  public setActionText(actionText: string): void { this.actionText = actionText; }
  public setActionIcon(actionIcon: string): void { this.actionIcon = actionIcon; }
}
